/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { PatternPropType } from '@web-stories-wp/patterns';
import { ResourcePropTypes } from '@web-stories-wp/media';
import { AnimationProps } from '@web-stories-wp/animation';

/**
 * Internal dependencies
 */
import { OverlayType } from './utils/overlay';
import {
  BACKGROUND_TEXT_MODE,
  MULTIPLE_VALUE,
  TEXT_SHADOW_STYLE,
  TEXT_SHADOW_TARGET,
} from './constants';

export const PageSizePropType = PropTypes.shape({
  width: PropTypes.number,
  height: PropTypes.number,
  containerHeight: PropTypes.number,
});

const StoryPropTypes = {};

export const BackgroundAudioPropType = PropTypes.shape({
  id: PropTypes.number,
  src: PropTypes.string,
  mimeType: PropTypes.string,
});

StoryPropTypes.story = PropTypes.shape({
  storyId: PropTypes.number,
  title: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  author: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
  }),
  slug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  modified: PropTypes.string.isRequired,
  excerpt: PropTypes.string.isRequired,
  featuredMedia: PropTypes.shape({
    id: PropTypes.number.isRequired,
    url: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  password: PropTypes.string.isRequired,
  currentStoryStyles: PropTypes.object,
  autoAdvance: PropTypes.bool,
  defaultPageDuration: PropTypes.number,
  backgroundAudio: BackgroundAudioPropType,
});

StoryPropTypes.mask = PropTypes.shape({
  type: PropTypes.string.isRequired,
});

StoryPropTypes.link = PropTypes.shape({
  url: PropTypes.string.isRequired,
  desc: PropTypes.string,
  icon: PropTypes.string,
});

StoryPropTypes.size = PropTypes.exact({
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
});

StoryPropTypes.box = PropTypes.exact({
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  rotationAngle: PropTypes.number.isRequired,
});

StoryPropTypes.page = PropTypes.shape({
  id: PropTypes.string.isRequired,
  animations: PropTypes.arrayOf(PropTypes.shape(AnimationProps)),
  elements: PropTypes.arrayOf(PropTypes.shape(StoryPropTypes.element)),
  overlay: PropTypes.oneOf(Object.values(OverlayType)),
  backgroundAudio: BackgroundAudioPropType,
});

const StoryLayerPropTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

StoryPropTypes.flip = PropTypes.shape({
  vertical: PropTypes.bool,
  horizontal: PropTypes.bool,
});

const textBoxAttachmentOffsets = {
  targetId: PropTypes.string.isRequired,
  alignment: PropTypes.oneOf(['start', 'center', 'end']).isRequired,
  gap: PropTypes.number.isRequired,
  crossAxisOffset: PropTypes.number.isRequired,
  coordinateSpace: PropTypes.oneOf(['target-local']).isRequired,
};

const TextBoxAttachmentPropType = PropTypes.oneOfType([
  PropTypes.exact({
    ...textBoxAttachmentOffsets,
    version: PropTypes.oneOf([1, 2]).isRequired,
    targetEdge: PropTypes.oneOf(['top']).isRequired,
    ownEdge: PropTypes.oneOf(['bottom']).isRequired,
  }),
  PropTypes.exact({
    ...textBoxAttachmentOffsets,
    version: PropTypes.oneOf([3]).isRequired,
    targetEdge: PropTypes.oneOf(['bottom']).isRequired,
    ownEdge: PropTypes.oneOf(['top']).isRequired,
  }),
]);

const StoryElementPropTypes = {
  id: PropTypes.string.isRequired,
  groupId: PropTypes.string,
  type: PropTypes.string.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  flip: StoryPropTypes.flip,
  rotationAngle: PropTypes.number.isRequired,
  mask: StoryPropTypes.mask,
  link: StoryPropTypes.link,
  blendMode: PropTypes.string,
  opacity: PropTypes.number,
  lockAspectRatio: PropTypes.bool,
  isBackground: PropTypes.bool,
  textBoxAttachment: TextBoxAttachmentPropType,
  overlay: PatternPropType,
  overlays: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      pattern: PatternPropType.isRequired,
      isHidden: PropTypes.bool,
      blur: PropTypes.number,
    })
  ),
};

const StoryMediaPropTypes = {
  scale: PropTypes.number.isRequired,
  focalX: PropTypes.number,
  focalY: PropTypes.number,
};

const DynamicCropPositionPropType = PropTypes.shape({
  timestamp: PropTypes.number.isRequired,
  xPositionPercent: PropTypes.number.isRequired,
  y: PropTypes.number,
  isPausedMovement: PropTypes.bool,
  isUserEdited: PropTypes.bool,
});

const DynamicCropPropType = PropTypes.shape({
  aspectRatio: PropTypes.string.isRequired,
  height: PropTypes.number,
  y: PropTypes.number,
  positions: PropTypes.arrayOf(DynamicCropPositionPropType).isRequired,
});

StoryPropTypes.element = PropTypes.shape(StoryElementPropTypes);

StoryPropTypes.layer = PropTypes.shape(StoryLayerPropTypes);

StoryPropTypes.elements = {};

StoryPropTypes.elements.image = PropTypes.shape({
  backdropBlur: PropTypes.number,
  backdropEdgeSoftness: PropTypes.number,
  ...StoryElementPropTypes,
  ...StoryMediaPropTypes,
  resource: ResourcePropTypes.imageResource,
  lutFilter: PropTypes.shape({
    versionId: PropTypes.string.isRequired,
    intensity: PropTypes.number.isRequired,
  }),
});

StoryPropTypes.elements.video = PropTypes.shape({
  ...StoryElementPropTypes,
  ...StoryMediaPropTypes,
  resource: ResourcePropTypes.videoResource,
  poster: PropTypes.string,
  tracks: PropTypes.arrayOf(ResourcePropTypes.trackResource),
  loop: PropTypes.bool,
  dynamicCrop: DynamicCropPropType,
});

StoryPropTypes.elements.gif = PropTypes.shape({
  ...StoryElementPropTypes,
  ...StoryMediaPropTypes,
  resource: ResourcePropTypes.gifResource,
});

StoryPropTypes.elements.media = PropTypes.oneOfType([
  StoryPropTypes.elements.image,
  StoryPropTypes.elements.video,
  StoryPropTypes.elements.gif,
]);

export const AnimationPropType = PropTypes.shape(AnimationProps);

const FontMetricsPropType = PropTypes.shape({
  upm: PropTypes.number.isRequired,
  asc: PropTypes.number.isRequired,
  des: PropTypes.number.isRequired,
  tAsc: PropTypes.number,
  tDes: PropTypes.number,
  tLGap: PropTypes.number,
  wAsc: PropTypes.number,
  wDes: PropTypes.number,
  xH: PropTypes.number,
  capH: PropTypes.number,
  yMin: PropTypes.number,
  yMax: PropTypes.number,
  hAsc: PropTypes.number,
  hDes: PropTypes.number,
  lGap: PropTypes.number,
});

const FontVariantPropType = PropTypes.arrayOf(PropTypes.number);

export const FontPropType = PropTypes.shape({
  family: PropTypes.string,
  service: PropTypes.string,
  weights: PropTypes.arrayOf(PropTypes.number),
  styles: PropTypes.arrayOf(PropTypes.string),
  // There's no built-in prop type validation for tuples.
  variants: PropTypes.arrayOf(FontVariantPropType),
  fallbacks: PropTypes.arrayOf(PropTypes.string),
  metrics: FontMetricsPropType,
  url: PropTypes.string,
  urls: PropTypes.arrayOf(
    PropTypes.shape({
      variant: FontVariantPropType.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  fsSelection: PropTypes.number,
  useTypoMetrics: PropTypes.bool,
});

const TextFontReferenceIdentityPropType = PropTypes.oneOfType([
  PropTypes.shape({
    kind: PropTypes.oneOf(['asset']).isRequired,
    sourceId: PropTypes.string.isRequired,
  }),
  PropTypes.shape({
    kind: PropTypes.oneOf(['metadata']).isRequired,
    fingerprint: PropTypes.string.isRequired,
  }),
]);

const TextFontReferencesPropType = PropTypes.shape({
  version: PropTypes.oneOf([1]).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      identity: TextFontReferenceIdentityPropType.isRequired,
      revision: PropTypes.string.isRequired,
      font: FontPropType.isRequired,
    })
  ).isRequired,
});

export const PaddingPropType = PropTypes.shape({
  horizontal: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([MULTIPLE_VALUE]),
  ]),
  vertical: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([MULTIPLE_VALUE]),
  ]),
  locked: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf([MULTIPLE_VALUE]),
  ]),
});

const StoryTextElementPropTypes = {
  content: PropTypes.string,
  backgroundTextMode: PropTypes.oneOf(Object.values(BACKGROUND_TEXT_MODE)),
  textBoxBackdropBlur: PropTypes.number,
  textBoxBackdropEdgeSoftness: PropTypes.number,
  backgroundColor: PatternPropType,
  color: PatternPropType,
  textInnerGlow: PropTypes.shape({
    version: PropTypes.oneOf([1]).isRequired,
    enabled: PropTypes.bool.isRequired,
    color: PropTypes.string.isRequired,
    opacity: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    choke: PropTypes.number.isRequired,
    range: PropTypes.number.isRequired,
    blendMode: PropTypes.oneOf([
      'normal',
      'multiply',
      'screen',
      'overlay',
      'darken',
      'lighten',
      'color-dodge',
      'color-burn',
      'hard-light',
      'soft-light',
      'difference',
      'exclusion',
      'linear-burn',
    ]).isRequired,
    technique: PropTypes.oneOf(['softer']).isRequired,
    source: PropTypes.oneOf(['edge']).isRequired,
    contour: PropTypes.oneOf(['linear']).isRequired,
    noise: PropTypes.oneOf([0]).isRequired,
    jitter: PropTypes.oneOf([0]).isRequired,
    antiAlias: PropTypes.oneOf([false]).isRequired,
  }),
  textDepth: PropTypes.oneOfType([
    PropTypes.shape({
      version: PropTypes.oneOf([1]).isRequired,
      mode: PropTypes.oneOf(['off', 'raised', 'recessed']).isRequired,
      size: PropTypes.number.isRequired,
      lightDirection: PropTypes.number.isRequired,
      softness: PropTypes.number.isRequired,
      strength: PropTypes.number.isRequired,
    }),
    PropTypes.shape({
      version: PropTypes.oneOf([2]).isRequired,
      mode: PropTypes.oneOf(['off', 'raised', 'recessed']).isRequired,
      style: PropTypes.oneOf(['inner-bevel']).isRequired,
      technique: PropTypes.oneOf(['chisel-hard']).isRequired,
      size: PropTypes.number.isRequired,
      depth: PropTypes.number.isRequired,
      lightDirection: PropTypes.number.isRequired,
      altitude: PropTypes.number.isRequired,
      softness: PropTypes.number.isRequired,
      highlight: PropTypes.shape({
        color: PropTypes.string.isRequired,
        opacity: PropTypes.number.isRequired,
        blendMode: PropTypes.oneOf([
          'normal',
          'multiply',
          'screen',
          'overlay',
          'darken',
          'lighten',
          'color-dodge',
          'color-burn',
          'hard-light',
          'soft-light',
          'difference',
          'exclusion',
          'linear-burn',
        ]).isRequired,
      }).isRequired,
      shadow: PropTypes.shape({
        color: PropTypes.string.isRequired,
        opacity: PropTypes.number.isRequired,
        blendMode: PropTypes.oneOf([
          'normal',
          'multiply',
          'screen',
          'overlay',
          'darken',
          'lighten',
          'color-dodge',
          'color-burn',
          'hard-light',
          'soft-light',
          'difference',
          'exclusion',
          'linear-burn',
        ]).isRequired,
      }).isRequired,
      glossContour: PropTypes.oneOf(['linear']).isRequired,
      contour: PropTypes.shape({
        enabled: PropTypes.bool.isRequired,
        profile: PropTypes.oneOf(['linear', 'ring']).isRequired,
        range: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ]),
  textTexture: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    scale: PropTypes.number,
    fallbackColor: PatternPropType,
    sizing: PropTypes.oneOf(['cover', 'contain', 'stretch']),
    position: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
    }),
  }),
  font: FontPropType.isRequired,
  fontSize: PropTypes.number,
  fontReferences: TextFontReferencesPropType,
  lineHeight: PropTypes.number,
  listStyle: PropTypes.shape({
    version: PropTypes.oneOf([1]).isRequired,
    marker: PropTypes.shape({
      preset: PropTypes.oneOf(['disc', 'circle', 'square', 'custom'])
        .isRequired,
      glyph: PropTypes.string,
      color: PatternPropType,
      size: PropTypes.number,
      sizeRatio: PropTypes.number,
      baselineShift: PropTypes.number,
      horizontalShift: PropTypes.number,
    }).isRequired,
    indent: PropTypes.number.isRequired,
    nestedIndent: PropTypes.number.isRequired,
    markerGap: PropTypes.number.isRequired,
    itemSpacing: PropTypes.number.isRequired,
  }),
  padding: PaddingPropType,
  textAlign: PropTypes.string,
  textShadowStyle: PropTypes.oneOf(Object.values(TEXT_SHADOW_STYLE)),
  textShadowX: PropTypes.number,
  textShadowY: PropTypes.number,
  textShadowBlur: PropTypes.number,
  textShadowColor: PatternPropType,
  shadowTarget: PropTypes.oneOf(Object.values(TEXT_SHADOW_TARGET)),
  verticalAnchor: PropTypes.oneOf(['top', 'center', 'bottom']),
};

StoryPropTypes.textContent = PropTypes.shape({
  ...StoryTextElementPropTypes,
});

StoryPropTypes.elements.text = PropTypes.shape({
  ...StoryElementPropTypes,
  ...StoryTextElementPropTypes,
});

StoryPropTypes.elements.shape = PropTypes.shape({
  backdropBlur: PropTypes.number,
  backdropEdgeSoftness: PropTypes.number,
  ...StoryElementPropTypes,
  backgroundColor: PatternPropType,
});

StoryPropTypes.elements.sticker = PropTypes.shape({
  ...StoryElementPropTypes,
  sticker: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }),
});

const LottiePlaybackPropType = PropTypes.shape({
  autoplay: PropTypes.bool.isRequired,
  loop: PropTypes.bool.isRequired,
  loopCount: PropTypes.number,
  startDelay: PropTypes.number.isRequired,
  holdDuration: PropTypes.number,
  speed: PropTypes.number.isRequired,
  endBehavior: PropTypes.oneOf(['freeze', 'reset', 'hide']).isRequired,
});

const LottieParameterBindingPropType = PropTypes.oneOfType([
  PropTypes.shape({
    kind: PropTypes.oneOf(['slot']).isRequired,
    slotId: PropTypes.string.isRequired,
    slotType: PropTypes.string,
  }),
  PropTypes.shape({
    kind: PropTypes.oneOf(['jsonPointer']).isRequired,
    jsonPointer: PropTypes.string.isRequired,
  }),
]);

const LottieParameterValuePropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.arrayOf(PropTypes.number),
]);

const LottieParameterSchemaPropType = PropTypes.shape({
  version: PropTypes.oneOf([1]).isRequired,
  parameters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.oneOf(['text', 'number', 'color']).isRequired,
      required: PropTypes.bool,
      constraints: PropTypes.shape({
        maxLength: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
      }),
      bindings: PropTypes.arrayOf(LottieParameterBindingPropType).isRequired,
    })
  ).isRequired,
});

const LottieParameterValuesPropType = PropTypes.objectOf(
  LottieParameterValuePropType
);

const LottieFontPropType = PropTypes.shape({
  family: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  format: PropTypes.oneOf(['woff', 'woff2', 'truetype', 'opentype']).isRequired,
  weight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf(['normal', 'bold']),
  ]).isRequired,
  style: PropTypes.oneOf(['normal', 'italic']).isRequired,
});

StoryPropTypes.elements.lottie = PropTypes.shape({
  ...StoryElementPropTypes,
  lottieContractVersion: PropTypes.oneOf([1]).isRequired,
  animationId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  definition: PropTypes.object.isRequired,
  intrinsicWidth: PropTypes.number.isRequired,
  intrinsicHeight: PropTypes.number.isRequired,
  fps: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  parameterSchema: LottieParameterSchemaPropType,
  defaultParameterValues: LottieParameterValuesPropType,
  parameterValues: LottieParameterValuesPropType,
  fonts: PropTypes.arrayOf(LottieFontPropType).isRequired,
  playback: LottiePlaybackPropType.isRequired,
  previewImageUrl: PropTypes.string,
});

StoryPropTypes.elements.background = PropTypes.shape({
  ...StoryLayerPropTypes,
  inner: StoryPropTypes.element,
});

export default StoryPropTypes;

/**
 * Page object.
 *
 * @typedef {Page} Page
 * @property {Element[]} elements Array of all elements.
 */

/**
 * Element object
 *
 * @typedef {Element} Element A story element
 * @property {string} id  A unique uuid for the element
 * @property {string} type The type of the element, e.g. video, gif, image
 * @property {number} x The x position of the element, its top left corner
 * @property {number} y The y position of the element, its top left corner
 * @property {number} width The width of the element
 * @property {number} height The height of the element
 * @property {Object} flip If the element has been flipped vertical/horizontal
 * @property {number} rotationAngle The element's rotation angle
 * @property {Object} mask The type of mask applied to the element
 * @property {Object} link The url, icon and description of a link applied to element
 * @property {number} opacity The opacity of the element
 * @property {boolean} lockAspectRatio Whether the element's aspect ratio is locked
 * @property {Resource} resource The element's resource object
 */

/**
 * Resource object
 *
 * @typedef {Resource} Resource Resource data for elements
 * @property {{ full: { height: number, width: number }, output: Object }} sizes The data for the full-size element
 * @property {boolean} local Whether the media was uploaded by the user
 * @property {boolean} isOptimized Whether the media was uploaded by the user
 * @property {boolean} isPlaceholder Whether the resource is a placeholder and not fully uploaded yet.
 * @property {string} src The source string for the resource
 */

/**
 * Story object.
 *
 * @typedef {Story} Story
 * @property {Object} story - A story object.
 * @property {number} storyId Story post id.
 * @property {string} title Story title.
 * @property {string} status Post status, draft or published.
 * @property {Array<Page>} pages Array of all pages.
 * @property {Object} author Story author.
 * @property {string} slug The slug of the story.
 * @property {string} date The publish date of the story.
 * @property {string} modified The modified date of the story.
 * @property {string} content AMP HTML content.
 * @property {string} excerpt Short description.
 * @property {Object} featuredMedia Featured media object.
 * @property {string} password Password
 */
